import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import {
  Field,
  FieldData,
  FieldDataValue,
  FieldType,
  PublicClassNames,
  RadioButton,
  RadioGroupFieldOptions,
} from 'embed/constants'
import { ValidationData } from 'common/services/Validation'

import { AddressAutocomplete } from 'embed/components/AddressAutocomplete'
import { ApplicationZipcodeField } from 'embed/components/ApplicationZipcodeField'
import { FreeformMultiple } from 'embed/components/FreeformMultiple'
import MultiButtonList from 'embed/components/MultiButtonList'
import SliderAmountInput from 'embed/components/SliderInput'
import { isNil } from 'embed/utils/object'
import ApplicationFieldRadioButton from '../../ApplicationFieldRadioButton'
import ButtonList from '../../ButtonList'
import IconButtonList from '../../IconButtonList'
import UniversityAutocomplete from '../../UniversityAutocomplete'
import { SelectInput } from './SelectInputField'
import { TextInput } from './TextInput'

const applicationFieldStyles = require('../styles')

export interface Props {
  borderRadiusSmall: string
  colorError: string
  field: Field
  fieldData?: FieldData
  fontSizeBase: string
  onAddressSelect: (addressOne: string, city: string, state: string, zip: string) => void
  onBlur: (value: React.FormEvent<HTMLInputElement>) => void
  onChange: (value: FieldDataValue) => void
  onKeyDown: (key: string) => void
  onUniversitySelect: (name: string) => void
  validationData: ValidationData
  forceFocus?: boolean
  useFloatingLabelInputs?: boolean
  tooltip?: React.ReactNode
}

const getInvalidOptionStyle = (colorError: string, field: Field, fieldData?: FieldData) => {
  const isValid = !!fieldData?.isValid
  const shouldShowIsInvalid = fieldData?.shouldShowValidationStatus === true && isValid === false
  let invalidValidationStyle
  if (field.options.required && shouldShowIsInvalid) {
    invalidValidationStyle = {
      border: `1px solid  ${colorError}`,
    }
  }
  return invalidValidationStyle ?? {}
}

export const getInputNumberValue = (value): number | undefined => {
  if (isNil(value)) {
    return undefined
  }
  const numberValue = +value
  return isNaN(numberValue) ? undefined : numberValue
}

const getInputPlaceholder = (options, validationData, useFloatingLabelInputs) => {
  const placeholder =
    typeof options.placeholder === 'function' ? options.placeholder(validationData) : options.placeholder

  return useFloatingLabelInputs ? '' : placeholder
}

const getMultipleListValue = (fieldData) => (Array.isArray(fieldData?.value) ? fieldData.value : undefined)

const getSliderMaxAmount = (maxAmount, validationData) =>
  typeof maxAmount === 'function' ? maxAmount(validationData) : maxAmount

export const InputField: React.FC<Props> = (props) => {
  const { colorError, field, fieldData, tooltip, useFloatingLabelInputs } = props

  const isLoading = props.fieldData?.isLoading ?? false

  const invalidValidationStyle = getInvalidOptionStyle(colorError, field, fieldData)

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => props.onKeyDown(event.key)

  const fieldValue = props.fieldData?.value ?? ''
  const textFieldProps = {
    autoComplete: props.field.options.autocomplete,
    disabled: isLoading,
    id: props.field.key,
    inputMode: props.field.options.inputMode,
    name: props.field.key,
    onBlur: (event: React.FormEvent<HTMLInputElement>) => {
      // Trim whitespace from the beginning and end of text input to avoid validation errors caused
      // but accidental spaces (including those added when filling out fields using autofill)
      const trimmedValue = (fieldValue as string).trim()
      if (trimmedValue !== fieldValue) {
        props.onChange(trimmedValue)
      }
      props.onBlur?.(event)
    },
    onChange: (event: React.FormEvent<HTMLInputElement>) => props.onChange(event.currentTarget.value),
    onKeyDown,
    style: invalidValidationStyle,
    type: props.field.type,
    value: fieldValue,
    autoFocus: props.forceFocus,
    placeholder: getInputPlaceholder(field.options, props.validationData, useFloatingLabelInputs),
  }

  const onBlur = (event: React.FormEvent<HTMLInputElement>) => textFieldProps.onBlur(event)
  const onChange = (event: React.FormEvent<HTMLInputElement>, value: React.ReactText) => props.onChange(value)

  const textInputClassName = classnames(
    PublicClassNames.FormInput,
    css`
      border-radius: ${props.borderRadiusSmall};
      font-size: ${props.fontSizeBase};
    `,
    {
      [css`
        background-color: transparent;
        color: transparent;
      `]: isLoading,
    },
    { [applicationFieldStyles.floatingLabelInput]: useFloatingLabelInputs },
  )

  let input
  switch (props.field.type) {
    case FieldType.SliderInput:
      input = (
        <SliderAmountInput
          name={props.field.key}
          labelTooltip={tooltip}
          label={props.field.options.label}
          onChange={props.onChange}
          minAmount={props.field.options.minAmount}
          maxAmount={getSliderMaxAmount(props.field.options.maxAmount, props.validationData)}
          canOverflowMaxAmount={props.field.options.canOverflowMaxAmount}
          value={getInputNumberValue(props.fieldData?.value)}
        />
      )
      break
    case FieldType.ButtonList:
      input = (
        <ButtonList
          name={props.field.key}
          onChange={props.onChange}
          options={props.field.options}
          value={props.fieldData?.value}
          forceFocus={props.forceFocus}
        />
      )
      break
    case FieldType.IconButtonList:
      input = (
        <IconButtonList
          name={props.field.key}
          onChange={props.onChange}
          options={props.field.options}
          value={props.fieldData?.value}
        />
      )
      break
    case FieldType.MultiButtonList:
      input = (
        <MultiButtonList
          name={props.field.key}
          onChange={props.onChange}
          options={props.field.options}
          value={getMultipleListValue(props.fieldData)}
        />
      )
      break
    case FieldType.RadioGroup:
      /**
       * pass in a radioGroupProp for pathToStore
       * if the store returns undefined at that location then use child.checked for defaultChecked
       * if the store returns a value, if child.value === the store value, set that as defaultChecked
       *
       */
      input = (props.field.options as RadioGroupFieldOptions).children.map((child: RadioButton) => {
        const radioButton = child
        const radioGroupProps = {
          child: radioButton,
          id: child.value,
          name: child.name,
          onChange: (event: React.FormEvent<HTMLInputElement>) => props.onChange(event.currentTarget.value),
          type: FieldType.Radio,
          value: child.value,
          forceFocus: props.forceFocus,
        }

        return <ApplicationFieldRadioButton key={`radio_${child.value}`} {...radioGroupProps} />
      })
      break
    case FieldType.Number:
      const inputProps = { ...textFieldProps, value: textFieldProps.value as number }
      input = <input className={textInputClassName} required={useFloatingLabelInputs} {...inputProps} />
      break
    case FieldType.Zipcode:
      input = (
        <ApplicationZipcodeField className={textInputClassName} required={useFloatingLabelInputs} {...textFieldProps} />
      )
      break
    case FieldType.Text:
      input = <TextInput {...props} textInputClassName={textInputClassName} textFieldProps={textFieldProps} />
      break
    case FieldType.AddressAutocomplete:
      input = (
        <AddressAutocomplete
          containerClass={classnames({
            [applicationFieldStyles.inputFilled]: (textFieldProps.value as string).length > 0,
          })}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onAddressSelect={props.onAddressSelect}
          placeholder={textFieldProps.placeholder}
          styles={{
            customInputStyles: invalidValidationStyle,
            field: '',
            fieldError: '',
            fieldFocus: applicationFieldStyles.inputFocus,
            fieldInput: textInputClassName,
            fieldLabel: '',
          }}
          value={textFieldProps.value as string}
          fieldKey={props.field.key}
          forceFocus={props.forceFocus}
        />
      )
      break
    case FieldType.UniversityAutocomplete:
      input = (
        <UniversityAutocomplete
          containerClass={classnames({
            [applicationFieldStyles.inputFilled]: (textFieldProps.value as string).length > 0,
          })}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onUniversitySelect={props.onUniversitySelect}
          placeholder={textFieldProps.placeholder}
          styles={{
            customInputStyles: invalidValidationStyle,
            field: '',
            fieldError: '',
            fieldFocus: applicationFieldStyles.inputFocus,
            fieldInput: textInputClassName,
            fieldLabel: '',
          }}
          value={textFieldProps.value as string}
          fieldKey={props.field.key}
          forceFocus={props.forceFocus}
        />
      )
      break
    case FieldType.Password:
      const passwordFieldProps = {
        ...textFieldProps,
        onKeyDown: textFieldProps.onBlur,
        value: textFieldProps.value as string,
      }
      input = <input className={textInputClassName} {...passwordFieldProps} />
      break
    case FieldType.FreeformMultipleText:
      input = (
        <FreeformMultiple
          name={props.field.key}
          onChange={props.onChange}
          options={props.field.options}
          value={getMultipleListValue(props.fieldData)}
          field={props.field}
          fieldData={props.fieldData}
          useFloatingLabel={useFloatingLabelInputs}
        />
      )
      break
    case FieldType.Select:
    case FieldType.DynamicSelect:
      input = <SelectInput {...props} />
      break
    default:
      return null
  }

  return input
}
